import {Injectable} from '@angular/core';
import {
  OnDestroyMixin,
  untilComponentDestroyed,
} from '@w11k/ngx-componentdestroyed';
import {throwError} from 'rxjs';
import {
  catchError,
  debounceTime,
  map,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import {AppState} from '.';
import {ClientPresentationState} from '../account/my-account/model/ui-state/ClientPresentationState';
import {InsightsUiState} from '../account/my-account/model/ui-state/InsightsUiState.model';
import {SortUiState} from '../account/my-account/model/ui-state/SortUiState.model';
import {HttpService} from '../services/http.service';
import {UserService} from '../services/shared/user.service';
import AssetOrderBy from '../shared/enums/AssetOrderBy.enum';
import FundOrderBy from '../shared/enums/FundOrderBy.enum';
import {GpAssetsPageTabs} from '../shared/enums/GpAssetsPageTabs.enum';
import {GpFundsPageTabs} from '../shared/enums/GpFundsPageTabs.enum';
import HoldingDiscriminator from '../shared/enums/HoldingDiscriminator.enum';
import InvestorsOrderBy from '../shared/enums/InvestorsOrderBy.enum';
import {AppQuery} from './app.query';
import {AppStore} from './app.store';

@Injectable({providedIn: 'root'})
export class AppService extends OnDestroyMixin {
  private CLIENT_PRESENTATION_STATE_UPDATE_ENDPOINT =
    this.userService.ACCOUNT_ENDPOINT + '/update-client-presentation-state';

  accountDetails$ = this.userService.accountDetails$;

  constructor(
    private http: HttpService,
    private query: AppQuery,
    private appStore: AppStore,
    private userService: UserService
  ) {
    super();
    this.getInitialStateFromServer()
      .pipe(
        tap((initialState) => {
          this.updateGPClientPresentationState(initialState);
        }),
        tap(() => this.trackAndPersistClientPrefsChanges())
      )
      .subscribe();
  }

  updateInsightState(insightState: InsightsUiState) {
    this.appStore.update((state) => {
      state = this.cloneStateForSortUpdate(state);
      state.gp.uiPrefs.insightsUiState = {
        ...insightState,
      };
      return state;
    });
  }

  updateAssetsSortState(sortOrder: AssetOrderBy) {
    this.appStore.update((state) => {
      state = this.cloneStateForSortUpdate(state);
      state.gp.uiPrefs.assets = {
        ...state.gp.uiPrefs.assets,
        assetsOrderBy: sortOrder,
      };
      return state;
    });
  }

  updateAssetsActiveTabState(activeTab: GpAssetsPageTabs) {
    this.appStore.update((state) => {
      state = this.cloneStateForSortUpdate(state);
      state.gp.uiPrefs.assets = {
        ...state.gp.uiPrefs.assets,
        activeTab: activeTab,
      };
      return state;
    });
  }

  updateFundsSortState(sortOrder: FundOrderBy) {
    this.appStore.update((state) => {
      state = this.cloneStateForSortUpdate(state);
      state.gp.uiPrefs.funds = {
        ...state.gp.uiPrefs.funds,
        fundsOrderBy: sortOrder,
      };
      return state;
    });
  }

  updateFundsActiveTabState(activeTab: GpFundsPageTabs) {
    this.appStore.update((state) => {
      state = this.cloneStateForSortUpdate(state);
      state.gp.uiPrefs.funds = {
        ...state.gp.uiPrefs.funds,
        activeTab: activeTab,
      };
      return state;
    });
  }

  updatePromoUIState(key: string, value: boolean) {
    this.appStore.update((state) => {
      state = this.cloneStateForSortUpdate(state);
      state.gp.uiPrefs.promo = {
        ...state.gp.uiPrefs.promo,
        [key]: value,
      };
      return state;
    });
  }

  updateOfferingDeckEngagementSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'offeringDeckEngagementUiState');
  }

  updatePositionsReportSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'positionsReport');
  }

  updateContactsSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'contacts');
  }

  updateRecipientContactsSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'recipientContacts');
  }

  updatePaymentsSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'payments');
  }

  updateDocumentsSortParams(updatedSort: SortUiState) {
    this.appStore.update((state) => {
      state = this.cloneStateForSortUpdate(state);
      state.gp.uiPrefs.documents = {
        ...state.gp.uiPrefs.documents,
        sort: {...updatedSort},
      };
      return state;
    });
  }

  updateDocumentsSkipStepParams(skipPrepareStep: boolean) {
    this.appStore.update((state) => {
      state = this.cloneStateForSortUpdate(state);
      state.gp.uiPrefs.documents = {
        ...state.gp.uiPrefs.documents,
        skipPrepareStep,
      };
      return state;
    });
  }

  updateBankAccountsSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'gpBankAccounts');
  }

  updateInvestorsBankAccountsSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'investorsBankAccounts');
  }

  updateBankApplicationsSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'bankApplications');
  }

  updateFundsManagingOverviewSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'fundsManagingOverview');
  }

  updateFundsManagingPortfolioSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'fundsManagingPortfolio');
  }

  updateAssetInvestorOverviewSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'assetInvestorOverview');
  }

  updateFundFundraisingSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'fundsFundraising');
  }

  updateAssetFundraisingSortState(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'assetsFundraising');
  }

  updateDistributionSortState(
    updatedSort: SortUiState,
    sender: HoldingDiscriminator
  ) {
    if (sender === HoldingDiscriminator.Fund) {
      this.simpleSortUpdate(updatedSort, 'fundsDistribution');
    } else if (sender === HoldingDiscriminator.Asset) {
      this.simpleSortUpdate(updatedSort, 'assetsDistribution');
    }
  }

  updateInvestorsSortOrder(sortOrder: InvestorsOrderBy) {
    this.appStore.update((state) => {
      state = this.cloneStateForSortUpdate(state);
      state.gp.uiPrefs.fundraising = {
        ...state.gp.uiPrefs.fundraising,
        assetDetailsInvestorsSortBy: sortOrder,
      };
      return state;
    });
  }

  updateInsightsInvestorInteractionsSortOrder(updatedSort: SortUiState) {
    this.simpleSortUpdate(updatedSort, 'insightsInvestorInteractions');
  }

  /** Used for updating things that only have a simple orderBy and direction */
  private simpleSortUpdate(
    updatedSort: SortUiState,
    keyToUpdate:
      | 'offeringDeckEngagementUiState'
      | 'positionsReport'
      | 'contacts'
      | 'recipientContacts'
      // | 'documents'
      | 'gpBankAccounts'
      | 'investorsBankAccounts'
      | 'bankApplications'
      | 'fundsManagingOverview'
      | 'fundsManagingPortfolio'
      | 'assetInvestorOverview'
      | 'fundsFundraising'
      | 'assetsFundraising'
      | 'fundsDistribution'
      | 'assetsDistribution'
      | 'insightsInvestorInteractions'
      | 'payments'
  ) {
    this.appStore.update((state) => {
      state = this.cloneStateForSortUpdate(state);
      state.gp.uiPrefs[keyToUpdate] = {
        ...state.gp.uiPrefs[keyToUpdate],
        sort: {...updatedSort},
      };
      return state;
    });
  }

  private cloneStateForSortUpdate(currentState): AppState {
    return {
      ...currentState,
      gp: {
        ...currentState.gp,
        uiPrefs: {
          ...currentState.gp.uiPrefs,
        },
      },
    } as AppState;
  }

  private trackAndPersistClientPrefsChanges() {
    this.query
      .select((state) => state.gp.uiPrefs)
      .pipe(
        skip(1),
        untilComponentDestroyed(this),
        debounceTime(1000),
        switchMap((uiPrefs) => {
          return this.clientPresentationStateServerUpdate(uiPrefs);
        })
      )
      .subscribe();
  }

  private updateGPClientPresentationState(uiPrefs: ClientPresentationState) {
    this.appStore.update((s) => {
      return {
        ...s,
        gp: {
          ...s.gp,
          uiPrefs: {
            ...s.gp.uiPrefs,
            ...uiPrefs,
          },
        },
      };
    });
  }

  private getInitialStateFromServer() {
    return this.userService.accountDetails$.pipe(
      take(1),
      map((accountDetails) => accountDetails.clientPresentationState)
    );
  }

  private clientPresentationStateServerUpdate(
    newUiPrefs: ClientPresentationState
  ) {
    // different endpoints for LP and GP
    const endpoint = this.CLIENT_PRESENTATION_STATE_UPDATE_ENDPOINT;
    return this.http
      .postTyped<boolean>(endpoint, JSON.stringify(newUiPrefs))
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }
}
